import {useQuery, useMutation} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {message} from "antd";
import {SalesInsight} from "../domain/sales-insight.entity";
import {AttendeeAnalytics} from "../domain/attendee-analytics.entity";
import {AttendeeDetails} from "../domain/attendee-details.entity";
import type {PaginationInfo} from "../domain/pagination.types";
import {
  createLiveEventAnalyticsRepository,
  ExportAttendeesCSVParams,
} from "../data/repositories";
import {PaginationMeta} from "../../../redux/service/i-service";
import {AnalyticsEvent} from "../../../utils/analytics";

/**
 * Query keys for live event analytics caching.
 */
export const LIVE_EVENT_ANALYTICS_QUERY_KEYS = {
  all: ["live-event-analytics"] as const,
  salesInsights: (eventId: string | number) =>
    [...LIVE_EVENT_ANALYTICS_QUERY_KEYS.all, "sales-insights", eventId] as const,
  attendees: (eventId: string | number, page: number, perPage: number) =>
    [
      ...LIVE_EVENT_ANALYTICS_QUERY_KEYS.all,
      "attendees",
      eventId,
      page,
      perPage,
    ] as const,
  attendeeDetails: (
    eventId: string | number,
    attendeeId?: string | number,
    attendeeEmail?: string
  ) =>
    [
      ...LIVE_EVENT_ANALYTICS_QUERY_KEYS.all,
      "attendee-details",
      eventId,
      attendeeId,
      attendeeEmail,
    ] as const,
};

/**
 * Hook for fetching sales insights (revenue + attendees count) for a live event.
 *
 * Returns a SalesInsight domain entity with methods like:
 * - getPrimaryRevenue() - for single-currency display
 * - hasMultipleCurrencies() - to show/hide breakdown link
 * - getRevenueBreakdown() - for multi-currency modal
 */
export const useGetSalesInsights = (eventId?: string | number) => {
  const repository = createLiveEventAnalyticsRepository();

  return useQuery<SalesInsight, AxiosError<{status: boolean; message: string}>>({
    queryKey: LIVE_EVENT_ANALYTICS_QUERY_KEYS.salesInsights(eventId ?? ""),
    queryFn: () => repository.getSalesInsights(eventId!),
    enabled: !!eventId,
  });
};

/**
 * Converts domain PaginationInfo to legacy PaginationMeta for CustomPagination.
 * This is the documented contingency approach from the PRD — keeping CustomPagination
 * on the v1 shape and deriving it from the domain type.
 */
function toPaginationMeta(pagination: PaginationInfo): PaginationMeta {
  const totalPages = Math.ceil(pagination.total / pagination.perPage) || 1;
  return {
    current_page: pagination.page,
    per_page: pagination.perPage,
    total_pages: totalPages,
    next_page: pagination.hasNext ? pagination.page + 1 : 0,
    previous_page: pagination.hasPrev ? pagination.page - 1 : 0,
  };
}

/**
 * Result type for useGetAttendees that includes both domain entities and legacy pagination.
 */
export interface UseGetAttendeesResult {
  attendees: AttendeeAnalytics[];
  pagination: PaginationInfo;
  legacyMeta: PaginationMeta;
}

/**
 * Hook for fetching paginated attendees list for a live event.
 *
 * Returns AttendeeAnalytics domain entities with methods like:
 * - fullName - computed firstName + lastName
 * - isPaid() - true when amount > 0
 * - key - stable React list key (reference)
 *
 * Also provides legacyMeta for CustomPagination consumption.
 */
export const useGetAttendees = (
  eventId?: string | number,
  page: number = 1,
  perPage: number = 5
) => {
  const repository = createLiveEventAnalyticsRepository();

  return useQuery<UseGetAttendeesResult, AxiosError<{status: boolean; message: string}>>({
    queryKey: LIVE_EVENT_ANALYTICS_QUERY_KEYS.attendees(eventId ?? "", page, perPage),
    queryFn: async () => {
      const result = await repository.getAttendees(eventId!, page, perPage);
      return {
        attendees: result.attendees,
        pagination: result.pagination,
        legacyMeta: toPaginationMeta(result.pagination),
      };
    },
    enabled: !!eventId,
    keepPreviousData: true,
  });
};

/**
 * Parameters for useGetAttendeeDetails hook.
 */
export interface UseGetAttendeeDetailsParams {
  eventId?: string | number;
  attendeeId?: string | number;
  attendeeEmail?: string;
  inlineAttendee?: AttendeeAnalytics;
}

/**
 * Hook for fetching details for a single attendee.
 *
 * V1: Makes network call using attendeeId + attendeeEmail.
 * V2: Returns wrapped inlineAttendee with no network call.
 *
 * Returns AttendeeDetails domain entity with methods like:
 * - hasBookingQuestions() - to conditionally render booking questions section
 * - hasPurchaseHistory() - to conditionally render purchase history section
 * - hasPhoneNumber() - to conditionally render phone field
 * - getDisplayPrice() - for showing price (uses callPrice on v1, amount on v2)
 */
export const useGetAttendeeDetails = (params: UseGetAttendeeDetailsParams) => {
  const {eventId, attendeeId, attendeeEmail, inlineAttendee} = params;
  const repository = createLiveEventAnalyticsRepository();

  return useQuery<AttendeeDetails, AxiosError<{status: boolean; message: string}>>({
    queryKey: LIVE_EVENT_ANALYTICS_QUERY_KEYS.attendeeDetails(
      eventId ?? "",
      attendeeId,
      attendeeEmail
    ),
    queryFn: () =>
      repository.getAttendeeDetails({
        eventId: eventId!,
        attendeeId,
        attendeeEmail,
        inlineAttendee,
      }),
    enabled: !!eventId && (!!inlineAttendee || (!!attendeeId && !!attendeeEmail)),
  });
};

/**
 * Hook for exporting attendees list as CSV file.
 *
 * Returns a mutation that:
 * - Fires success analytics event on completion
 * - Fires fail analytics event and shows error toast on failure
 *
 * Consumer is responsible for creating the download link from the returned Blob.
 */
export const useExportAttendeesCSV = () => {
  const repository = createLiveEventAnalyticsRepository();

  return useMutation<Blob, AxiosError<{message: string}>, ExportAttendeesCSVParams>({
    mutationFn: (params) => repository.exportAttendeesCSV(params),
    onError: (error) => {
      message.error(error.message);
      AnalyticsEvent("Coach attendee", "Coach attendee export CSV fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Coach attendee", "Coach attendee export CSV success");
    },
  });
};
